import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '@material-ui/core';
import { debounce } from 'lodash';
import { ErrorLabel } from 'design-system';
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';

// Models
import PersonValidationResponse from 'src/models/responseTypes/PersonValidationResponse';

// Functions
import { validateRuc } from '../functions';

export const instructionsLabel: React.ReactElement = (
  <Typography id="sales-web-inspection-label-instructions" variant="caption">
    Sin espacios ni guiones (ej. 3XXXXXXXXX), tal como aparece en tu aviso de
    operaciones.
  </Typography>
);

export const loadingLabel: React.ReactElement = (
  <Typography id="sales-web-inspection-label-loading" variant="caption">
    Validando información...
  </Typography>
);

export type UseLegalEntityValidatorProps = {
  entityData: PersonValidationResponse;
  identificationNumber: string;
  identificationType: string;
  personSearchRequestStatus: RequestIndicatorTypes;
  onPersonSearchRequested: (identification: string, idType: string) => void;
};

export type DebounceSearchEntityType = (
  param: string,
  type: string,
  validationError: string,
) => void;

export default function useLegalEntityValidator({
  entityData,
  identificationNumber,
  identificationType,
  personSearchRequestStatus,
  onPersonSearchRequested,
}: UseLegalEntityValidatorProps): {
  entityIsInvalid: boolean;
  messageLabel: React.ReactElement;
  formatError: string;
  idCheckRequested: boolean;
} {
  const [formatError, updateFormatError] = useState('');
  const [previousId, updatePreviousId] = useState('');
  const [invalidEntityId, toggleInvalidEntityId] = useState(false);
  const [messageLabel, updateMessageLabel] =
    useState<React.ReactElement>(instructionsLabel);
  const [idCheckRequested, toggleIdCheckRequested] = useState(false);

  /**
   * Maintain a reference to the debounced search method. Note that a debounced method
   * will only fire after not being invoked for a certain amount of time (in this case,
   * 1.8 seconds).
   */
  const debouncedSearchEntity = useRef(
    debounce<DebounceSearchEntityType>((query, idType, validationError) => {
      if (query.length >= 3) {
        updatePreviousId(`${query}${idType}`);
        if (validationError) {
          toggleInvalidEntityId(true);
        } else {
          toggleInvalidEntityId(false);
          onPersonSearchRequested(query, idType);
          toggleIdCheckRequested(true);
        }
      }
    }, 1800),
  ).current;

  /**
   * Update the validation error
   */
  useEffect(() => {
    updateFormatError(validateRuc(identificationNumber, identificationType));
  }, [identificationNumber, identificationType]);

  /**
   * Call the debounced search method each time one of these values change.
   */
  useEffect(() => {
    debouncedSearchEntity(
      identificationNumber,
      identificationType,
      formatError,
    );
  }, [
    debouncedSearchEntity,
    identificationNumber,
    identificationType,
    formatError,
  ]);

  /**
   * This effect will manage the message label display logic. The logic is as follows:
   * - If a validation error occurs, display the validation error message.
   * - If the person doesn't exists, display a generic welcome.
   * - If the person exists and the data matches, display a "welcome back" message
   */
  useEffect(() => {
    if (previousId !== `${identificationNumber}${identificationType}`) {
      return;
    }

    if (personSearchRequestStatus === 'requesting') {
      updateMessageLabel(loadingLabel);
      return;
    }

    if (formatError) {
      updateMessageLabel(<ErrorLabel>{formatError}</ErrorLabel>);
      return;
    }

    updateMessageLabel(instructionsLabel);
  }, [
    identificationNumber,
    personSearchRequestStatus,
    updateMessageLabel,
    previousId,
    identificationType,
    formatError,
  ]);

  /**
   * We just need to expose personIsValid and messageLabel. Everything else is considered
   * "internal" implementation and should not be exposed to the rest of the application.
   */
  return {
    entityIsInvalid: invalidEntityId || !entityData.isValidPerson,
    messageLabel,
    formatError,
    idCheckRequested,
  };
}
